import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { api } from '@/lib/api-client'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { useState, useEffect } from 'react'
import { Save, RotateCcw, UserX, UserMinus, ArrowDownCircle, Package, Copy } from 'lucide-react'

interface RulesConfig {
  tenant_id: string
  rule_inactive_enabled: number
  rule_disabled_enabled: number
  rule_downgrade_enabled: number
  rule_excess_enabled: number
  rule_redundant_enabled: number
  inactive_days_threshold: number
  inactive_high_severity_days: number
  disabled_days_threshold: number
  downgrade_max_calls: number
  downgrade_max_meetings: number
  downgrade_active_days: number
  excess_pct_threshold: number
  excess_min_units: number
  excess_reducible_pct: number
  dismiss_cooldown_days: number
  expiration_days: number
}

const DEFAULTS: Omit<RulesConfig, 'tenant_id'> = {
  rule_inactive_enabled: 1,
  rule_disabled_enabled: 1,
  rule_downgrade_enabled: 1,
  rule_excess_enabled: 1,
  rule_redundant_enabled: 1,
  inactive_days_threshold: 90,
  inactive_high_severity_days: 180,
  disabled_days_threshold: 0,
  downgrade_max_calls: 0,
  downgrade_max_meetings: 3,
  downgrade_active_days: 90,
  excess_pct_threshold: 20,
  excess_min_units: 5,
  excess_reducible_pct: 50,
  dismiss_cooldown_days: 90,
  expiration_days: 365,
}

function Toggle({ enabled, onChange, disabled }: { enabled: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!enabled)}
      className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200"
      style={{
        backgroundColor: enabled ? 'var(--color-brand-secondary)' : 'var(--color-gray-300)',
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <span
        className="pointer-events-none inline-block h-5 w-5 rounded-full shadow ring-0 transition-transform duration-200"
        style={{
          backgroundColor: 'white',
          transform: enabled ? 'translateX(1.25rem)' : 'translateX(0)',
        }}
      />
    </button>
  )
}

function NumberField({
  label, value, onChange, suffix, min, max, help,
}: {
  label: string; value: number; onChange: (v: number) => void
  suffix?: string; min?: number; max?: number; help?: string
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value) || 0)}
          min={min ?? 0}
          max={max}
          className="w-24 rounded-md border px-3 py-2 text-sm tabular-nums"
          style={{ borderColor: 'var(--color-border)' }}
        />
        {suffix && <span className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{suffix}</span>}
      </div>
      {help && <p className="mt-1 text-xs" style={{ color: 'var(--color-text-tertiary)' }}>{help}</p>}
    </div>
  )
}

export function AdminClientRecommendationRulesPage() {
  const { clientId } = useParams({ strict: false }) as { clientId: string }
  const queryClient = useQueryClient()

  const { data: tenant } = useQuery({
    queryKey: ['admin', 'tenants', clientId],
    queryFn: () => api.get<{ name: string }>(`/admin/tenants/${clientId}`),
  })

  const { data: config, isLoading } = useQuery({
    queryKey: ['admin', 'tenants', clientId, 'recommendation-rules'],
    queryFn: () => api.get<RulesConfig>(`/admin/tenants/${clientId}/recommendation-rules`),
  })

  const [form, setForm] = useState<Omit<RulesConfig, 'tenant_id'>>(DEFAULTS)
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    if (config) {
      const { tenant_id: _, ...rest } = config as RulesConfig & Record<string, unknown>
      setForm({ ...DEFAULTS, ...rest })
      setDirty(false)
    }
  }, [config])

  const update = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((f) => ({ ...f, [key]: value }))
    setDirty(true)
  }

  const saveMutation = useMutation({
    mutationFn: () => api.put(`/admin/tenants/${clientId}/recommendation-rules`, form),
    onSuccess: () => {
      toast.success('Configuracion de reglas guardada')
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', clientId, 'recommendation-rules'] })
      setDirty(false)
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const resetToDefaults = () => {
    setForm(DEFAULTS)
    setDirty(true)
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Recomendaciones" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 animate-pulse rounded-lg" style={{ backgroundColor: 'var(--color-gray-200)' }} />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Reglas de recomendaciones — ${tenant?.name ?? ''}`}
        actions={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => window.location.href = `/admin/clients/${clientId}`}>
              Volver al cliente
            </Button>
            <Button variant="secondary" onClick={resetToDefaults}>
              <RotateCcw className="h-3.5 w-3.5" /> Restaurar por defecto
            </Button>
            <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending} disabled={!dirty}>
              <Save className="h-3.5 w-3.5" /> Guardar cambios
            </Button>
          </div>
        }
      />

      {/* Rule 1: Inactive users */}
      <Card>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg"
              style={{ backgroundColor: 'var(--color-warning-light)' }}>
              <UserX className="h-5 w-5" style={{ color: 'var(--color-warning-dark)' }} />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Regla 1: Usuarios inactivos
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Detecta licencias asignadas a usuarios que no han iniciado sesion en un periodo determinado
              </p>
            </div>
          </div>
          <Toggle enabled={!!form.rule_inactive_enabled} onChange={(v) => update('rule_inactive_enabled', v ? 1 : 0)} />
        </div>
        {!!form.rule_inactive_enabled && (
          <div className="mt-4 grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-2" style={{ borderColor: 'var(--color-border)' }}>
            <NumberField
              label="Dias de inactividad"
              value={form.inactive_days_threshold}
              onChange={(v) => update('inactive_days_threshold', v)}
              suffix="dias"
              min={7}
              help="Se genera recomendacion si el usuario no inicia sesion en este periodo"
            />
            <NumberField
              label="Dias para severidad alta"
              value={form.inactive_high_severity_days}
              onChange={(v) => update('inactive_high_severity_days', v)}
              suffix="dias"
              min={30}
              help="Superado este umbral la severidad pasa de media a alta"
            />
          </div>
        )}
      </Card>

      {/* Rule 2: Disabled accounts */}
      <Card>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg"
              style={{ backgroundColor: 'var(--color-error-light)' }}>
              <UserMinus className="h-5 w-5" style={{ color: 'var(--color-error-dark)' }} />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Regla 2: Cuentas deshabilitadas
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Detecta licencias asignadas a cuentas deshabilitadas en Azure AD. Severidad siempre critica.
              </p>
            </div>
          </div>
          <Toggle enabled={!!form.rule_disabled_enabled} onChange={(v) => update('rule_disabled_enabled', v ? 1 : 0)} />
        </div>
        {!!form.rule_disabled_enabled && (
          <div className="mt-4 grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-2" style={{ borderColor: 'var(--color-border)' }}>
            <NumberField
              label="Dias deshabilitada antes de avisar"
              value={form.disabled_days_threshold}
              onChange={(v) => update('disabled_days_threshold', v)}
              suffix="dias"
              min={0}
              help="0 = avisa inmediatamente. Si se indica un valor, solo genera recomendacion si la cuenta lleva deshabilitada al menos estos dias."
            />
          </div>
        )}
      </Card>

      {/* Rule 3: Downgrade underutilized */}
      <Card>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg"
              style={{ backgroundColor: 'var(--color-brand-primary-50)' }}>
              <ArrowDownCircle className="h-5 w-5" style={{ color: 'var(--color-brand-primary)' }} />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Regla 3: Downgrade licencias premium
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Detecta usuarios con licencias premium que no usan funciones avanzadas (Teams llamadas/reuniones)
              </p>
            </div>
          </div>
          <Toggle enabled={!!form.rule_downgrade_enabled} onChange={(v) => update('rule_downgrade_enabled', v ? 1 : 0)} />
        </div>
        {!!form.rule_downgrade_enabled && (
          <div className="mt-4 grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-3" style={{ borderColor: 'var(--color-border)' }}>
            <NumberField
              label="Max llamadas Teams"
              value={form.downgrade_max_calls}
              onChange={(v) => update('downgrade_max_calls', v)}
              suffix="llamadas"
              min={0}
              help="Se recomienda downgrade si el usuario tiene <= este numero de llamadas"
            />
            <NumberField
              label="Max reuniones Teams"
              value={form.downgrade_max_meetings}
              onChange={(v) => update('downgrade_max_meetings', v)}
              suffix="reuniones"
              min={0}
              help="Se recomienda downgrade si el usuario tiene < este numero de reuniones"
            />
            <NumberField
              label="Dias actividad reciente"
              value={form.downgrade_active_days}
              onChange={(v) => update('downgrade_active_days', v)}
              suffix="dias"
              min={7}
              help="Solo aplica a usuarios activos en los ultimos N dias"
            />
          </div>
        )}
      </Card>

      {/* Rule 4: Excess purchased */}
      <Card>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg"
              style={{ backgroundColor: 'var(--color-success-light)' }}>
              <Package className="h-5 w-5" style={{ color: 'var(--color-success-dark)' }} />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Regla 4: Licencias sobrantes
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Detecta licencias compradas en exceso que no estan asignadas a ningun usuario
              </p>
            </div>
          </div>
          <Toggle enabled={!!form.rule_excess_enabled} onChange={(v) => update('rule_excess_enabled', v ? 1 : 0)} />
        </div>
        {!!form.rule_excess_enabled && (
          <div className="mt-4 grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-3" style={{ borderColor: 'var(--color-border)' }}>
            <NumberField
              label="Umbral % no asignadas"
              value={form.excess_pct_threshold}
              onChange={(v) => update('excess_pct_threshold', v)}
              suffix="%"
              min={5}
              max={90}
              help="Se detecta si el % de licencias sin asignar supera este umbral"
            />
            <NumberField
              label="Minimo unidades libres"
              value={form.excess_min_units}
              onChange={(v) => update('excess_min_units', v)}
              suffix="unidades"
              min={1}
              help="Solo se genera si hay al menos esta cantidad de unidades sin asignar"
            />
            <NumberField
              label="% reduccion conservadora"
              value={form.excess_reducible_pct}
              onChange={(v) => update('excess_reducible_pct', v)}
              suffix="%"
              min={10}
              max={100}
              help="Porcentaje de las licencias sobrantes que se propone reducir"
            />
          </div>
        )}
      </Card>

      {/* Rule 5: Redundant licenses */}
      <Card>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg"
              style={{ backgroundColor: 'var(--color-brand-accent-2-light, var(--color-gray-100))' }}>
              <Copy className="h-5 w-5" style={{ color: 'var(--color-brand-accent-2, var(--color-text-secondary))' }} />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Regla 5: Licencias redundantes
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Detecta usuarios con licencias duplicadas donde una ya incluye las funciones de la otra
              </p>
            </div>
          </div>
          <Toggle enabled={!!form.rule_redundant_enabled} onChange={(v) => update('rule_redundant_enabled', v ? 1 : 0)} />
        </div>
        {!!form.rule_redundant_enabled && (
          <div className="mt-4 border-t pt-4" style={{ borderColor: 'var(--color-border)' }}>
            <p className="text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
              Pares de licencias redundantes detectados automaticamente: E5 + E3, E5 + Power BI Pro, E5 + EMS E5, E3 + EMS E3, E3 + E1, E5 + Business Essentials.
            </p>
          </div>
        )}
      </Card>

      {/* Lifecycle settings */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg"
            style={{ backgroundColor: 'var(--color-gray-100)' }}>
            <RotateCcw className="h-5 w-5" style={{ color: 'var(--color-text-secondary)' }} />
          </div>
          <div>
            <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Ciclo de vida
            </h3>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Configuracion de tiempos para la gestion automatica de recomendaciones
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-2" style={{ borderColor: 'var(--color-border)' }}>
          <NumberField
            label="Cooldown al descartar"
            value={form.dismiss_cooldown_days}
            onChange={(v) => update('dismiss_cooldown_days', v)}
            suffix="dias"
            min={1}
            help="Tras descartar una recomendacion, no se vuelve a generar durante este periodo"
          />
          <NumberField
            label="Expiracion automatica"
            value={form.expiration_days}
            onChange={(v) => update('expiration_days', v)}
            suffix="dias"
            min={7}
            help="Las recomendaciones pendientes expiran automaticamente tras este periodo"
          />
        </div>
      </Card>

      {/* Sticky save bar */}
      {dirty && (
        <div className="sticky bottom-4 flex items-center justify-between rounded-lg border px-4 py-3 shadow-lg"
          style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-brand-secondary)' }}>
          <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
            Hay cambios sin guardar
          </p>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => { setForm({ ...DEFAULTS, ...(config ? (() => { const { tenant_id: _, ...r } = config as RulesConfig & Record<string, unknown>; return r })() : {}) }); setDirty(false) }}>
              Descartar
            </Button>
            <Button size="sm" onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
              <Save className="h-3.5 w-3.5" /> Guardar
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
